import { useState, useEffect } from 'react';
import { Box, Popover, Slider, Stack, Tooltip, IconButton as MuiIconButton, Typography } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import IconButton from 'components/@extended/IconButton';
import useHeaderTheme from 'hooks/useHeaderTheme';
import notificationSound from '../../../../assets/sounds/notifSound.mp3';

export default function SoundToggle() {
  const [volume, setVolume] = useState(50);
  const [previousVolume, setPreviousVolume] = useState(50);
  const [anchorEl, setAnchorEl] = useState(null);
  const { iconColor, hoverColor } = useHeaderTheme();

  useEffect(() => {
    const rawVolume = localStorage.getItem('notifSoundVolume');
    if (rawVolume !== null) {
      setVolume(parseInt(rawVolume, 10));
    }
  }, []);

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    localStorage.setItem('notifSoundVolume', newValue);
  };

  const playPreviewSound = (event, newValue) => {
    if (newValue > 0) {
      try {
        const audio = new Audio(notificationSound);
        audio.volume = newValue / 100;
        audio.play().catch((e) => console.warn('Preview sound blocked', e));
      } catch (err) {
        console.error('Failed to play preview', err);
      }
    }
  };

  const toggleMute = () => {
    if (volume > 0) {
      setPreviousVolume(volume);
      setVolume(0);
      localStorage.setItem('notifSoundVolume', 0);
    } else {
      const newVol = previousVolume > 0 ? previousVolume : 50;
      setVolume(newVol);
      localStorage.setItem('notifSoundVolume', newVol);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const isMuted = volume === 0;

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <Tooltip title="Notification Sound Settings">
        <IconButton
          color="secondary"
          variant="light"
          sx={(theme) => ({
            color: iconColor,
            bgcolor: open ? hoverColor : 'transparent',
            '&:hover': { bgcolor: hoverColor },
            ...theme.applyStyles('dark', { bgcolor: open ? hoverColor : 'transparent' })
          })}
          aria-label="sound toggle"
          onClick={handleClick}
        >
          {isMuted ? <VolumeOffIcon sx={{ color: iconColor }} /> : <VolumeUpIcon sx={{ color: iconColor }} />}
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Stack spacing={2} direction="row" alignItems="center">
            <MuiIconButton onClick={toggleMute} size="small" color={isMuted ? 'default' : 'primary'}>
              {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </MuiIconButton>
            <Slider aria-label="Volume" value={volume} onChange={handleVolumeChange} onChangeCommitted={playPreviewSound} color="primary" />
            <Typography variant="body2" sx={{ minWidth: 35, textAlign: 'right' }}>
              {volume}%
            </Typography>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}
